import { HTMLAttributes, ReactNode } from "react";
import { cn } from "@app/lib/utils";

export const cardSurfaceClassName =
  "rounded-lg border border-border bg-card text-card-foreground shadow-soft";

export const cardInteractiveClassName =
  "transition-all duration-150 hover:border-foreground/20";

export const cardHeaderClassName =
  "border-b border-border px-4 py-3.5 md:px-5 md:py-4";

export const cardContentPadding = {
  none: "",
  small: "p-3.5 md:p-4",
  medium: "p-4 md:p-5",
  large: "p-5 md:p-7",
};

export function Card({
  children,
  className,
  interactive,
  ...props
}: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        cardSurfaceClassName,
        interactive && cardInteractiveClassName,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
  children,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={cn(cardHeaderClassName, className)} {...props}>
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 text-start">
          {title && (
            <h3 className="text-start text-sm font-bold leading-6 text-foreground md:text-base md:leading-7">
              {title}
            </h3>
          )}
          {description && (
            <p className="mt-0.5 text-start text-xs leading-4 text-muted-foreground md:text-sm md:leading-5">
              {description}
            </p>
          )}
          {children}
        </div>
        {action && <div className="w-full shrink-0 sm:w-auto">{action}</div>}
      </div>
    </div>
  );
}

export function CardContent({
  children,
  className,
  padding = "medium",
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  padding?: keyof typeof cardContentPadding;
}) {
  return (
    <div className={cn(cardContentPadding[padding], className)} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "border-t border-border px-4 py-3.5 md:px-5 md:py-4",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardIcon({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/10 md:h-11 md:w-11 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:stroke-[2]",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}
